import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, Mail, Phone, Calendar, Save, Camera } from 'lucide-react';
import DefaultSEO from '../components/DefaultSEO';

const CompleteProfilePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [userPhoto, setUserPhoto] = useState(null);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dob: '',
        gender: 'Male'
    });

    useEffect(() => {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            navigate('/login', { replace: true });
            return;
        }

        // Fetch profile from backend
        const fetchProfile = async () => {
            const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
            try {
                const res = await fetch(`${apiBase}/auth/me`, {
                    headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.success && data.data) {
                        const u = data.data;
                        const nameParts = (u.name || '').split(' ');
                        setFormData(prev => ({
                            ...prev,
                            firstName: nameParts[0] || '',
                            lastName: nameParts.slice(1).join(' ') || '',
                            email: (u.email && !u.email.includes('@phone.zoyotrip.local')) ? u.email : '',
                            phone: u.phone || '',
                            dob: u.dob ? u.dob.split('T')[0] : prev.dob,
                            gender: u.gender || prev.gender,
                        }));
                        if (u.avatar) setUserPhoto(u.avatar);
                    }
                } else if (res.status === 401) {
                    localStorage.removeItem('auth_token');
                    navigate('/login', { replace: true });
                }
            } catch (err) {
                console.error('Error fetching profile:', err);
            }
        };

        fetchProfile();
    }, [navigate]);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const fullName = `${formData.firstName} ${formData.lastName}`.trim();

            const token = localStorage.getItem('auth_token');
            if (token) {
                const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
                await fetch(`${apiBase}/auth/profile`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        name: fullName,
                        email: formData.email,
                        dob: formData.dob,
                        gender: formData.gender,
                        phone: formData.phone
                    })
                });
            }

            setLoading(false);
            const from = location.state?.from;
            if (from) {
                navigate(from.pathname || from, { replace: true, state: from.state });
            } else {
                navigate('/account', { replace: true });
            }
        } catch (error) {
            console.error("Error updating profile:", error);
            setLoading(false);
        }
    };

    return (
        <div className="container py-4 min-vh-100 d-flex flex-column align-items-center justify-content-center">
            <DefaultSEO title="Complete Profile | ZoyoTrip" />
            <h2 className="fw-bold mb-2">Complete Your Profile</h2>
            <p className="text-muted mb-4 text-center">Please provide your details to finish setting up your account.</p>

            <div className="card shadow-sm border-0 w-100" style={{ maxWidth: '600px' }}>
                <div className="card-body p-4 p-md-5">

                    {/* Profile Picture Section */}
                    <div className="d-flex flex-column align-items-center mb-4">
                        <div className="position-relative">
                            {userPhoto ? (
                                <img src={userPhoto} alt="Profile" className="rounded-circle shadow-sm object-fit-cover" style={{ width: '100px', height: '100px' }} />
                            ) : (
                                <div className="bg-light rounded-circle shadow-sm d-flex justify-content-center align-items-center" style={{ width: '100px', height: '100px' }}>
                                    <User size={40} className="text-secondary" />
                                </div>
                            )}
                            <button className="btn btn-primary rounded-circle position-absolute bottom-0 end-0 p-2 shadow-sm d-flex align-items-center justify-content-center" style={{ width: '35px', height: '35px' }}>
                                <Camera size={16} />
                            </button>
                        </div>
                        <p className="text-muted small mt-2">Update Photo</p>
                    </div>

                    <form onSubmit={handleSave}>
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label className="form-label text-muted small fw-semibold">First Name</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-white"><User size={18} className="text-muted" /></span>
                                    <input type="text" className="form-control" name="firstName" value={formData.firstName} onChange={handleInputChange} required />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label text-muted small fw-semibold">Last Name</label>
                                <input type="text" className="form-control" name="lastName" value={formData.lastName} onChange={handleInputChange} required />
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label text-muted small fw-semibold">Email Address</label>
                            <div className="input-group">
                                <span className="input-group-text bg-white"><Mail size={18} className="text-muted" /></span>
                                <input type="email" className="form-control" name="email" value={formData.email} onChange={handleInputChange} placeholder="Enter your email address" />
                            </div>
                            <div className="form-text">Your primary email address.</div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label text-muted small fw-semibold">Phone Number</label>
                            <div className="input-group">
                                <span className="input-group-text bg-white"><Phone size={18} className="text-muted" /></span>
                                <input type="text" className="form-control" name="phone" value={formData.phone} disabled={!!formData.phone} onChange={handleInputChange} />
                            </div>
                        </div>

                        <div className="row g-3 mb-4">
                            <div className="col-md-6">
                                <label className="form-label text-muted small fw-semibold">Date of Birth</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-white"><Calendar size={18} className="text-muted" /></span>
                                    <input type="date" className="form-control" name="dob" value={formData.dob} onChange={handleInputChange} />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label text-muted small fw-semibold">Gender</label>
                                <select className="form-select" name="gender" value={formData.gender} onChange={handleInputChange}>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>

                        <button type="submit" className="btn btn-primary w-100 py-2.5 rounded-pill fw-bold d-flex align-items-center justify-content-center gap-2" disabled={loading}>
                            {loading ? 'Saving...' : <><Save size={18} /> Complete Registration</>}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CompleteProfilePage;
